import { journey, subscribeJourney } from "./journeyState";
import { prefersReducedMotion } from "./quality";

/* ── Panel registry ───────────────────────────────────────
   Every horizontal Panel registers itself once; Parallax
   layers register under their panel. ONE subscriber walks the
   registry on each journey update and writes transforms from
   cached offsets — no layout reads, no per-panel rAF. */

const panels = new Set();
let trackEl = null;
let unsub = null;

const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

/* Pure reveal math.
   center: panel center relative to viewport center (px, +right)
   width:  panel width (px); vw: viewport width (px) */
export function revealFor(center, width, vw) {
  // distance from viewport center to the nearest point of the
  // panel's "fully covering" span — wide panels don't dim mid-way
  const halfSpan = Math.max(width - vw, 0) / 2;
  const dist = Math.max(Math.abs(center) - halfSpan, 0);
  const far = dist > vw * 1.5;
  const t = Math.min(dist / vw, 1);
  const ease = t * t;
  return { opacity: 1 - 0.65 * ease, y: 24 * ease, far };
}

function measure(rec) {
  if (!trackEl || !rec.el) return;
  const tr = trackEl.getBoundingClientRect();
  const r = rec.el.getBoundingClientRect();
  rec.left = r.left - tr.left;
  rec.width = r.width;
}

export function measurePanels(track) {
  trackEl = track;
  panels.forEach(measure);
}

function resetPanel(rec) {
  if (rec.inner) {
    rec.inner.style.opacity = "";
    rec.inner.style.transform = "";
    rec.inner.style.visibility = "";
  }
  rec.layers.forEach((l) => {
    l.node.style.transform = "";
  });
}

function stopIfEmpty() {
  if (panels.size === 0 && unsub) {
    unsub();
    unsub = null;
  }
}

export function clearPanels() {
  panels.forEach(resetPanel);
  panels.clear();
  trackEl = null;
  stopIfEmpty();
}

function update() {
  const vw = window.innerWidth;
  const tx = journey.trackX;
  const reduced = prefersReducedMotion();

  panels.forEach((rec) => {
    const center = tx + rec.left + rec.width / 2 - vw / 2;
    const { opacity, y, far } = revealFor(center, rec.width, vw);

    if (far) {
      if (!rec.far) {
        rec.far = true;
        rec.inner.style.visibility = "hidden";
      }
      return;
    }
    if (rec.far) {
      rec.far = false;
      rec.inner.style.visibility = "";
    }

    if (!reduced) {
      rec.inner.style.opacity = opacity.toFixed(3);
      rec.inner.style.transform = `translate3d(0,${y.toFixed(1)}px,0)`;
    }

    rec.layers.forEach((l) => {
      const shift = clamp(center * l.depth, -l.maxShift, l.maxShift);
      l.node.style.transform = `translate3d(${shift.toFixed(2)}px,0,0)`;
    });
  });
}

function ensureLoop() {
  if (!unsub) unsub = subscribeJourney(update);
}

export function registerPanel(rec) {
  rec.layers = rec.layers || new Set();
  rec.far = false;
  rec.left = 0;
  rec.width = 0;
  panels.add(rec);
  measure(rec);
  ensureLoop();
  update();
  return () => {
    resetPanel(rec);
    panels.delete(rec);
    stopIfEmpty();
  };
}

export function registerLayer(rec, layer) {
  rec.layers.add(layer);
  update();
  return () => {
    layer.node.style.transform = "";
    rec.layers.delete(layer);
  };
}
